/**
 * Phase 11 — strict email verification gate surface.
 *
 * Authenticated but unverified users land here instead of the product.
 * Allows resend + sign-out only (change-email is deferred).
 */
import {
	Alert,
	Box,
	Button,
	Stack,
	Typography,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useState } from "react";

import { useAuth } from "../auth";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";

export function VerificationRequiredPage() {
	const { user, actions } = useAuth();
	const [resendState, setResendState] = useState<
		"idle" | "sending" | "sent" | "error"
	>("idle");
	const [error, setError] = useState<string | null>(null);

	usePageHeader({
		title: "Verify your email",
		subtitle: "Confirm your address to unlock FlahaSOIL",
		breadcrumbs: [{ label: "Home", to: "/" }, { label: "Verify email" }],
	});

	const handleResend = async () => {
		setResendState("sending");
		setError(null);
		try {
			await getApiClient().resendVerification();
			setResendState("sent");
		} catch (err) {
			setResendState("error");
			setError(err instanceof Error ? err.message : String(err));
		}
	};

	return (
		<Box sx={{ maxWidth: 520, mx: "auto", mt: { xs: 2, md: 4 } }}>
			<GlassSurface intensity="strong" depth="raised" sx={{ p: 3 }}>
				<Stack spacing={2}>
					<Typography
						variant="overline"
						sx={{
							color: "workspace.green",
							fontWeight: 700,
							letterSpacing: "0.1em",
						}}
					>
						Account verification
					</Typography>
					<Typography component="h1" variant="h5">
						Check your inbox
					</Typography>
					<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
						We sent a verification link to{" "}
						<strong>{user?.email ?? "your email"}</strong>. You must verify
						before accessing projects, soil tests, reports, or organization
						tools. Free accounts include five lifetime Preliminary tests after
						verification.
					</Typography>

					{resendState === "sent" && (
						<Alert severity="success">
							Verification email sent. Check your inbox and spam folder.
						</Alert>
					)}
					{error && (
						<Alert severity="error" onClose={() => setError(null)}>
							{error}
						</Alert>
					)}

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
						<Button
							variant="contained"
							startIcon={<MarkEmailReadIcon />}
							onClick={() => void handleResend()}
							disabled={resendState === "sending"}
						>
							{resendState === "sending" ? "Sending…" : "Resend verification"}
						</Button>
						<Button
							variant="outlined"
							onClick={() => void actions.refresh()}
						>
							I already verified
						</Button>
						<Button
							color="inherit"
							onClick={() => void actions.logout()}
						>
							Sign out
						</Button>
					</Stack>
				</Stack>
			</GlassSurface>
		</Box>
	);
}
